import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Target } from 'lucide-react';

interface RiskNode {
    name: string;
    value: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    children?: RiskNode[];
}

interface RadialRiskChartProps {
    data: RiskNode;
    title?: string;
    width?: number;
    height?: number;
}

const riskColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    critical: '#7c2d12',
};

/**
 * RadialRiskChart - D3.js powered sunburst chart for hierarchical risk visualization
 * Shows risk distribution across zones and sub-zones with interactive exploration
 */
export default function RadialRiskChart({
    data,
    title = 'Risk Distribution',
    width = 500,
    height = 500,
}: RadialRiskChartProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const { ref: containerRef, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const [hoveredNode, setHoveredNode] = useState<RiskNode | null>(null);
    const [selectedPath, setSelectedPath] = useState<string[]>([]);

    useEffect(() => {
        if (!svgRef.current || !isInView) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const radius = Math.min(width, height) / 2;
        const innerRadius = radius * 0.2;

        // Create hierarchy
        const root = d3.hierarchy(data)
            .sum(d => d.value)
            .sort((a, b) => (b.value || 0) - (a.value || 0));

        // Create partition layout
        const partition = d3.partition<RiskNode>()
            .size([2 * Math.PI, radius - innerRadius]);

        partition(root);

        // Arc generator
        const arc = d3.arc<d3.HierarchyRectangularNode<RiskNode>>()
            .startAngle(d => d.x0)
            .endAngle(d => d.x1)
            .padAngle(0.02)
            .padRadius(innerRadius)
            .innerRadius(d => innerRadius + d.y0)
            .outerRadius(d => innerRadius + d.y1 - 1);

        // Create main group
        const g = svg
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2})`);

        // Add paths for each node
        const paths = g.selectAll('path')
            .data(root.descendants().filter(d => d.depth > 0))
            .join('path')
            .attr('d', arc as any)
            .attr('fill', d => riskColors[d.data.riskLevel])
            .attr('fill-opacity', 0.85)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1)
            .style('cursor', 'pointer');

        // Animation
        if (!reducedMotion) {
            paths
                .attr('fill-opacity', 0)
                .attr('transform', 'scale(0.8)')
                .transition()
                .duration(800)
                .delay((_, i) => i * 30)
                .attr('fill-opacity', 0.85)
                .attr('transform', 'scale(1)');
        }

        // Hover effects
        paths
            .on('mouseover', function (event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('fill-opacity', 1)
                    .attr('transform', 'scale(1.02)');

                setHoveredNode(d.data);

                // Build path to root
                const path: string[] = [];
                let current: d3.HierarchyRectangularNode<RiskNode> | null = d;
                while (current) {
                    path.unshift(current.data.name);
                    current = current.parent;
                }
                setSelectedPath(path);
            })
            .on('mouseout', function () {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('fill-opacity', 0.85)
                    .attr('transform', 'scale(1)');

                setHoveredNode(null);
                setSelectedPath([]);
            });

        // Center circle with gradient
        const defs = svg.append('defs');
        const gradient = defs.append('radialGradient')
            .attr('id', 'centerGradient')
            .attr('cx', '50%')
            .attr('cy', '50%')
            .attr('r', '50%');

        gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#1e293b');

        gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#0f172a');

        g.append('circle')
            .attr('r', innerRadius - 5)
            .attr('fill', 'url(#centerGradient)')
            .attr('stroke', '#334155')
            .attr('stroke-width', 2);

        // Center text
        g.append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '-0.2em')
            .attr('fill', '#fff')
            .attr('font-size', '24px')
            .attr('font-weight', 'bold')
            .text(root.value?.toString() || '0');

        g.append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '1.2em')
            .attr('fill', '#94a3b8')
            .attr('font-size', '12px')
            .text('Total Risk Points');

    }, [data, width, height, isInView, reducedMotion]);

    return (
        <div
            ref={containerRef}
            className={`card-glass p-6 ${isInView ? 'animate-fadeIn' : 'opacity-0'}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                        <Target className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-display font-semibold text-charcoal-900">{title}</h3>
                        <p className="text-sm text-steel-500">Interactive sunburst view</p>
                    </div>
                </div>

                {/* Legend */}
                <div className="flex gap-3">
                    {(['low', 'medium', 'high', 'critical'] as const).map(level => (
                        <div key={level} className="flex items-center gap-1.5">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: riskColors[level] }}
                            />
                            <span className="text-xs text-steel-600 capitalize">{level}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex items-start gap-6">
                {/* Chart */}
                <div className="flex-shrink-0">
                    <svg
                        ref={svgRef}
                        width={width}
                        height={height}
                        style={{ overflow: 'visible' }}
                    />
                </div>

                {/* Info Panel */}
                <div className="flex-1 min-w-[200px]">
                    {hoveredNode ? (
                        <div className="p-4 bg-steel-50 rounded-xl border border-steel-200 animate-fadeIn">
                            {/* Breadcrumb */}
                            <div className="flex items-center gap-1 text-xs text-steel-500 mb-3">
                                {selectedPath.map((p, i) => (
                                    <span key={i} className="flex items-center">
                                        {i > 0 && <span className="mx-1">›</span>}
                                        <span className={i === selectedPath.length - 1 ? 'text-charcoal-700 font-medium' : ''}>
                                            {p}
                                        </span>
                                    </span>
                                ))}
                            </div>

                            <h4 className="font-semibold text-charcoal-900 text-lg mb-2">
                                {hoveredNode.name}
                            </h4>

                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-steel-600">Risk Points</span>
                                    <span className="font-medium text-charcoal-900">{hoveredNode.value}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-steel-600">Risk Level</span>
                                    <span
                                        className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                                        style={{
                                            backgroundColor: `${riskColors[hoveredNode.riskLevel]}20`,
                                            color: riskColors[hoveredNode.riskLevel],
                                        }}
                                    >
                                        {hoveredNode.riskLevel}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-steel-50/50 rounded-xl border border-dashed border-steel-200 text-center">
                            <p className="text-steel-500 text-sm">
                                Hover over segments to explore risk distribution
                            </p>
                        </div>
                    )}

                    {/* Quick Stats */}
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                            <div className="text-2xl font-bold text-red-600">3</div>
                            <div className="text-xs text-red-600/70">Critical Zones</div>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <div className="text-2xl font-bold text-amber-600">5</div>
                            <div className="text-xs text-amber-600/70">Needs Review</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
